// MessageBox component
import { Transition } from "@headlessui/react";
import { AnnotationIcon, BookmarkIcon } from "@heroicons/react/solid";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as reactResizer from "react-resize-detector";
import { AccountPage, SignUp } from "../components";
import { BookmarksPage } from "../components/BookmarksPage";
import { CommentList } from "../components/CommentList";
import Modal from "../components/Modal";
import { ReportPage } from "../components/ReportPage";
import { SettingsActions } from "../components/SettingsActions";
import { firebase, logout } from "../constants/firebase";
import { ReactComponent as Logo } from "../plasma-logo.svg";
import { Drawer } from "./Drawer";
import { UserContext } from "./UserProvider";

const { withResizeDetector } = reactResizer;

export const PopupContext = createContext({});

export const Popup = ({ chrome }) => {
  const db = firebase.firestore();
  const spacer = useRef(null);
  const user = useContext(UserContext);
  const [inputProps, setInputProps] = useState({
    isReply: false,
    commentId: null,
    subtitle: null,
  });
  const [showModal, setShowModal] = useState({ name: null, detail: null });
  const [thread, setThread] = useState("");
  const parent = useRef(null);

  useEffect(() => {
    const now = firebase.firestore.Timestamp.fromDate(new Date());
    let url = new URL(window.location.href);
    url = url.host + url.pathname;

    db.collection("threads")
      .where("url", "==", url)
      .get()
      .then((querySnapshot) => {
        if (!querySnapshot.docs.length) {
          db.collection("threads")
            .add({
              createdAt: now,
              updatedAt: now,
              url,
            })
            .then((doc) => {
              setThread(doc.id);
            });
        } else {
          const doc = querySnapshot.docs[0];
          setThread(doc.id);
        }
      });
  }, [db]);

  let url = new URL(window.location.href);
  url = url.origin + url.pathname;
  let hasBookmarkedThread = user?.bookmarkedThreads?.find(
    (thread) => thread.url === url
  );

  let title = document.title;

  const bookmarkThread = (e) => {
    let bookmarkedThreads = [];

    if (hasBookmarkedThread) {
      bookmarkedThreads = user.bookmarkedThreads?.filter(
        (bookmark) => bookmark.url !== url
      );
    } else {
      // bookmarkedThreads = Array.from(
      //   new Set([...(user.bookmarkedThreads || []), url])
      // );
      bookmarkedThreads = [...(user.bookmarkedThreads || []), { url, title }];
    }

    db.collection(`users`).doc(user.uid).update({
      bookmarkedThreads,
    });
  };

  useEffect(() => {
    if (user && !user.username) {
      setShowModal({ name: "signup", detail: null });
    }
  }, [user]);

  const closeModal = () => {
    setShowModal({ name: null, detail: null });
  };

  const DrawerDetector = withResizeDetector(Drawer);

  return (
    <PopupContext.Provider value={{ thread, chrome, showModal, setShowModal }}>
      <div
        id="parent"
        ref={parent}
        className="flex-1 overflow-hidden z-1"
        style={{ height: "95vh" }}
      >
        <div className="flex-none modal-handle rounded-xl w-full flex items-center justify-start h-16 px-4">
          <div className="flex-none text-2xl font-bold text-white py-4">
            <Logo className="w-32" />
          </div>
          <div className="flex-grow"> </div>
          <div className="flex-none" />
          {!!user?.username && (
            <>
              <div>
                <span className="tooltip" title="Feedback">
                  <a
                    href="https://forms.gle/m9nnTkJNukNqqxYu8"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <button className="mr-3 bg-gray-100 bg-opacity-10 hover:bg-opacity-30 w-9 h-9 rounded-full">
                      <AnnotationIcon className="w-5 h-5 text-white mx-auto" />
                    </button>
                  </a>
                </span>
                {/* <NotificationsContainer /> */}
              </div>
              <div>
                <span className="tooltip" title="Bookmark">
                  <button
                    type="button"
                    onClick={bookmarkThread}
                    className="mr-3 bg-gray-100 bg-opacity-10 hover:bg-opacity-30 w-9 h-9 rounded-full"
                  >
                    <BookmarkIcon
                      className={`w-5 h-5 mx-auto ${
                        hasBookmarkedThread ? "text-blue-500" : "text-white"
                      }`}
                    />
                  </button>
                </span>
              </div>
              <div>
                <SettingsActions />
              </div>
              <div className="has-tooltip">
                <button
                  type="button"
                  onClick={() =>
                    showModal.name === "account"
                      ? setShowModal({ name: null, detail: null })
                      : setShowModal({ name: "account", detail: null })
                  }
                >
                  <img
                    alt="profile"
                    src={
                      user.imageUrl ||
                      "https://qph.fs.quoracdn.net/main-qimg-2b21b9dd05c757fe30231fac65b504dd"
                    }
                    className="mx-auto object-cover rounded-full h-9 w-9 "
                  />
                </button>
              </div>
            </>
          )}
        </div>

        <div id="comments" className="px-2 flex-grow h-full overflow-auto ">
          <div className="d-flex justify-content-between flex-column  position-relative">
            <CommentList setInputProps={setInputProps} thread={thread} />
          </div>
        </div>
        <div id="spacer" ref={spacer} className="spacer flex-none ">
          {" "}
        </div>
        <Modal
          isOpen={showModal.name === "account"}
          closeModal={closeModal}
          title="My Account"
        >
          <AccountPage closeModal={closeModal} />
        </Modal>
        <Modal
          isOpen={showModal.name === "report"}
          closeModal={closeModal}
          title="Report"
        >
          <ReportPage closeModal={closeModal} />
        </Modal>
        <Modal
          isOpen={showModal.name === "bookmarks"}
          closeModal={closeModal}
          title="My Bookmarks"
        >
          <BookmarksPage />
        </Modal>
        <Modal
          isOpen={showModal.name === "signup"}
          closeModal={closeModal}
          title="Sign Up"
        >
          <SignUp closeModal={closeModal} />
        </Modal>
        <Transition
          show={showModal !== null}
          enter="transition-opacity duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DrawerDetector
            inputProps={inputProps}
            spacer={spacer}
            user={user}
            logout={logout}
            parent={parent}
          />
        </Transition>
      </div>
    </PopupContext.Provider>
  );
};
